import { create } from 'zustand';
import type { TrackInfo } from '@shared/types.ts';
import { realtime, nowTopic } from '@/lib/ws.ts';
import { player } from '@/lib/player.ts';

/**
 * Realtime push state — kind #2 of the four (see docs/ARCHITECTURE.md §9).
 *
 * Zustand, NOT React Context. Context re-renders every consumer on every
 * value change; a grid of twelve radio cards each updating on track change,
 * on a Fire TV Stick, is where the 30fps target dies. Here each card
 * subscribes to exactly its own channel's slice.
 *
 * Also not TanStack Query — Query is pull-shaped, and this is pushed.
 */

interface NowPlayingState {
  tracks: Record<string, TrackInfo | null>;
  set(channelId: string, track: TrackInfo | null): void;
}

export const useNowPlaying = create<NowPlayingState>((set) => ({
  tracks: {},
  set: (channelId, track) =>
    set((s) => ({ tracks: { ...s.tracks, [channelId]: track } })),
}));

/**
 * How long to hold a metadata frame before showing it.
 *
 * THE MODEL: now-playing describes the LIVE EDGE, and the listener is a buffer
 * behind the live edge. So a frame that arrives now describes audio that
 * reaches the speakers `latency` from now, and holding it that long makes the
 * change land with the sound.
 *
 * THE MISTAKE, worth leaving written down: the first version computed
 * `startedAt + latency` and held the frame until then. That looked more
 * precise and was much worse — it trusted the feed's own start timestamp,
 * which can be skewed or plain wrong, and a start time drifting into the
 * future turned a fourteen-second error into a minute-plus one. The feed's
 * clock is not our clock and was never worth importing. Only OUR latency
 * matters, and only as a duration.
 *
 * THE CEILING is doing real work, not being defensive. `getCurrentLiveOffset`
 * is an estimate, and for a live HLS stream with no programme-date-time tags
 * it can be nonsense. Metadata for these stations is never more than a few
 * seconds out for any reason we can correct, so anything past the ceiling is
 * a bad measurement rather than a big buffer, and the right response to a bad
 * measurement is to ignore it.
 */
const MAX_SYNC_MS = 30_000;

let loggedSync = '';

function displayDelayMs(channelId: string, _track: TrackInfo | null): number {
  if (player.getState().channel?.id !== channelId) return 0;

  /*
   * The FIRST frame for a channel is never held.
   *
   * The server sends a cached snapshot the instant you subscribe, precisely
   * so nobody stares at an empty metadata panel (docs/ARCHITECTURE.md §8,
   * rule 3). Delaying that would trade a small error for a blank panel, which
   * is worse. The delay exists to make TRANSITIONS land with the audio, and a
   * transition needs something to transition from.
   */
  if (!(channelId in useNowPlaying.getState().tracks)) return 0;

  const raw = player.latencyMs();
  const delay = raw > 0 && raw <= MAX_SYNC_MS ? raw : 0;

  // Say what was measured and what was used. This number was guessed at once
  // already and the guess made things worse; it should not be invisible.
  const line = `${Math.round(raw)}ms measured, holding ${Math.round(delay)}ms`;
  if (line !== loggedSync) {
    loggedSync = line;
    console.warn(`[n8m] metadata sync: ${line}`);
  }

  return delay;
}

/** Wire the socket into the store once, at app start. */
export function bindRealtime(): () => void {
  const timers = new Set<ReturnType<typeof setTimeout>>();
  /** Highest seq actually APPLIED per channel — held frames can land out of
   *  order when consecutive tracks carry different delays. */
  const applied = new Map<string, number>();

  const unsubscribe = realtime.onMessage((topic, data, seq) => {
    const channelId = topic.startsWith('now:') ? topic.slice(4) : null;
    if (!channelId) return;

    const apply = () => {
      if ((applied.get(channelId) ?? 0) > seq) return;
      applied.set(channelId, seq);
      useNowPlaying.getState().set(channelId, data);
    };

    const delay = displayDelayMs(channelId, data);
    if (delay <= 0) { apply(); return; }

    const timer = setTimeout(() => { timers.delete(timer); apply(); }, delay);
    timers.add(timer);
  });

  return () => {
    unsubscribe();
    for (const t of timers) clearTimeout(t);
    timers.clear();
  };
}

/**
 * Selector hook. Subscribing to ONE key means a track change on channel A
 * does not re-render the card for channel B.
 */
export function useTrack(channelId: string): TrackInfo | null {
  return useNowPlaying((s) => s.tracks[channelId] ?? null);
}

/** Subscribe to a channel's metadata for as long as a component is mounted. */
export function subscribeToChannel(channelId: string): () => void {
  return realtime.subscribe(nowTopic(channelId));
}

/**
 * The PLAYING channel holds its own subscription, independent of any view.
 *
 * Metadata used to be owned entirely by ChannelCard, which meant it existed
 * only while a card for that channel was on screen. Everything you actually
 * do while something plays takes that card away: switching to Live TV with
 * radio still going, or opening theater, where the grid is hidden. The mini
 * player then sat on "Live" forever — the one place still showing what's on
 * was the one place you could no longer see.
 *
 * Playback outlives the grid, so its subscription has to as well. The socket
 * refcounts topics, so the card and the player can both hold one.
 */
export function bindPlayerSubscription(): () => void {
  let currentId: string | null = null;
  let release: (() => void) | null = null;

  const apply = (id: string | null) => {
    if (id === currentId) return;
    release?.();
    release = null;
    currentId = id;
    if (id) release = realtime.subscribe(nowTopic(id));
  };

  const unsubscribe = player.subscribe((s) => {
    apply(s.channel?.capabilities.nowPlaying ? s.channel.id : null);
  });

  return () => { unsubscribe(); apply(null); };
}
